import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';

import { IDelta, ILiveServeUpd, IMarket, IOutcome, ISportEvent } from '@frontend/oxygen/shared-utils';

import { EnvironmentConfig } from '../../../client-config/index';
import { PubSubService } from '../../../core/services/communication/pubsub/pubsub.service';
import { DeviceService } from '../../../core/services/device/device.service';
import { StorageService } from '../../../core/services/storage/storage.service';
import { IPrcrCombo, IPreCreatedEvents } from '../models/pre-created-bet-builder.model';
import { PreCreatedAddToBetslipComponent } from '../preCreatedBetBuilderAddToBetslip/pre-created-add-to-betslip.component';

interface IUpdateData {
    delta: IDelta;
    update: ILiveServeUpd;
    type: string;
}
@Component({
    selector: 'pre-bet-builder-list-item',
    standalone: true,
    imports: [CommonModule, PreCreatedAddToBetslipComponent],
    templateUrl: './pre-bet-builder-list-item.component.html',
    styleUrls: ['./pre-bet-builder-list-item.component.scss'],
})
export class PreBetBuilderListItemComponent implements OnInit, OnDestroy {
    @Input() bet: IPrcrCombo;
    @Input() eventRef: ISportEvent;
    private readonly environment = inject(EnvironmentConfig).configuration;
    public isLadbrokes = this.environment.BRAND === 'ladbrokes';

    deviceService = inject(DeviceService);
    StorageService = inject(StorageService);
    pubsub = inject(PubSubService);

    ngOnInit() {
        this.pubsub.subscribe(`PreBetBuilderListItemComponent-${this.bet?.hashId}`, this.pubsub.API.UPDATE_TO_PRECREATED, (data: IUpdateData) => {
            this.updateStatus(data);
        });
    }

    /**
     * method for update market and selection status with sSELCN, sEVMKT
     * @param data:IUpdateData
     */
    private updateStatus(data: IUpdateData) {
        if (data.type === 'sSELCN') {
            this.updateOutcomeStatus(data);
        } else if (data.type === 'sEVMKT') {
            this.updateMarketStatus(data);
        }
    }

    /**
     * method for update market status
     * @param data:IUpdateData
     */
    private updateMarketStatus(data: IUpdateData) {
        if (!this.bet?.events) return;

        this.bet.isMktSuspended = false;

        this.bet.events.forEach((event: IPreCreatedEvents) => {
            if (!event.markets) return;

            event.markets.forEach((market: IMarket) => {
                const subjectNumber = data.update?.subject_number;

                if (subjectNumber && market.id === subjectNumber.toString()) {
                    market.marketStatusCode = data.delta.marketStatusCode ?? '';
                }

                if (market.marketStatusCode === 'S') {
                    this.bet.isMktSuspended = true;
                }
            });
        });
    }

    /**
     * method for update outcome status
     * @param data:IUpdateData
     */
    private updateOutcomeStatus(data: IUpdateData): void {
        const events = this.bet?.events;
        const subjectNumber = data.update?.subject_number;
        const status = data.delta?.status ?? '';

        if (!events || subjectNumber === undefined) return;

        this.bet.isOcmSuspended = false;

        events.forEach((event: IPreCreatedEvents) => {
            const markets = event.markets;
            if (!markets) return;

            markets.forEach((market: IMarket) => {
                market.outcomes.forEach((outcome: IOutcome) => {
                    if (outcome.id === subjectNumber.toString()) {
                        outcome.outcomeStatusCode = status;
                    }

                    if (outcome.outcomeStatusCode === 'S') {
                        this.bet.isOcmSuspended = true;
                    }
                });
            });
        });
    }

    /**
     * method for Check if the outcome is the last outcome of the last market in the current event
     * @param currentMarketInfo: ISportEvent
     * @param currentMarket: IMarket
     * @param currentOutcome: IOutcome
     * @returns {boolean}
     */
    public isLastOutcome(currentMarketInfo: ISportEvent, currentMarket: IMarket, currentOutcome: IOutcome): boolean {
        const markets = currentMarketInfo.markets;
        if (!markets || markets.length === 0) return false;

        const lastMarket = markets[markets.length - 1];
        const outcomes = lastMarket.outcomes;
        if (!outcomes || outcomes.length === 0) return false;

        const lastOutcome = outcomes[outcomes.length - 1];
        return currentMarket === lastMarket && currentOutcome.id === lastOutcome.id;
    }

    ngOnDestroy(): void {
        this.pubsub.unsubscribe(`PreBetBuilderListItemComponent-${this.bet?.hashId}`);
    }
}
